/**
 * The admin sidebar's nav DATA and the pure helpers over it.
 *
 * Split from `AdminSidebar.tsx` so that file exports only components — a module
 * exporting both loses React fast refresh, the same reason
 * `quota-account-meta.ts` and `addis-status-meta.ts` exist. It is also what lets
 * `AdminSidebar.test.ts` assert the nav's shape without mounting a sidebar that
 * needs Clerk, feature flags and the messaging context.
 */
import {
  ArrowPathRoundedSquareIcon,
  BoltIcon,
  ChartBarSquareIcon,
  ChartPieIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  CubeTransparentIcon,
  KeyIcon,
  LinkIcon,
  DocumentTextIcon,
  HeartIcon,
  NewspaperIcon,
  PowerIcon,
  RocketLaunchIcon,
  ServerStackIcon,
  SignalIcon,
  Squares2X2Icon,
  SunIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

export interface NavItemDef {
  name: string;
  href: string;
  icon: React.ElementType;
  exact?: boolean;
  v2Only?: boolean;
  /** The route prefixes this entry is active on, when more than its own `href` (A7Y-5210: HELM spans `/admin/cmo/crm`). */
  activePrefixes?: readonly string[];
}

/** Whether `pathname` is one of this entry's pages. */
export function isNavItemActive(item: NavItemDef, pathname: string): boolean {
  if (item.exact) return pathname === item.href;
  return (item.activePrefixes ?? [item.href]).some((prefix) => pathname.startsWith(prefix));
}

/** A section is force-expanded whenever the active route falls inside it, so collapsing a section never hides the page the user is on. */
export function sectionContainsActiveRoute(
  items: readonly NavItemDef[],
  pathname: string
): boolean {
  return items.some((item) => isNavItemActive(item, pathname));
}

/**
 * The three sections UKKO's work is split across (TPF, 2026-09-09).
 *
 * `ukkoNav` is the fleet itself — quota, workloads and slots are about the
 * machine rather than about any one system. `addisNav` and `aphidNav` are
 * top-level sections rather than sub-labels inside Ukko, which is also what
 * makes them collapsible: `NavSection` force-expands a section whenever the
 * active route is inside it, so nesting nine pages under one heading meant that
 * heading could never be collapsed while you were on any of them.
 *
 * Both families call their overview `Workflows`. Their sections disambiguate
 * it, and it is what each page lists.
 *
 * `exact: true` on ADDIS → Workflows is load-bearing. Its href
 * `/admin/automation` is a prefix of every other automation route, so without
 * it that row would highlight on CDID, ADGS and APD as well — and its section
 * would force-expand on all of them.
 */
export const ukkoNav: readonly NavItemDef[] = [
  { name: 'AI Quota', href: '/admin/automation/quotas', icon: BoltIcon },
  { name: 'Workloads', href: '/admin/automation/workloads', icon: ServerStackIcon },
  { name: 'Slots', href: '/admin/automation/slots', icon: Squares2X2Icon },
];

export const addisNav: readonly NavItemDef[] = [
  { name: 'Overview', href: '/admin/automation/overview', icon: PowerIcon },
  { name: 'Workflows', href: '/admin/automation', icon: CpuChipIcon, exact: true },
  { name: 'CDID', href: '/admin/automation/cdid', icon: ArrowPathRoundedSquareIcon },
  { name: 'ADGS', href: '/admin/automation/adgs', icon: BoltIcon },
  { name: 'APD', href: '/admin/automation/apd', icon: RocketLaunchIcon },
];

export const aphidNav: readonly NavItemDef[] = [
  { name: 'Workflows', href: '/admin/platform-health/aphid', icon: SignalIcon },
  { name: 'AHC', href: '/admin/platform-health/ahc', icon: HeartIcon },
];

/**
 * Yevhen's pages (A7Y-3870): Channel Console moves out of `dashboardNav`
 * into this section and HELM joins it — his daily/weekly bird's-eye views, not
 * general admin ops. D16: the route paths are unchanged (`/admin/channel-console`
 * stays put), only the sidebar grouping moves. Since A7Y-5210 HELM is one entry
 * over all of its views — Today, This week, Board and Accounts, the last three
 * still at `/admin/cmo/crm…` — so it is active on both prefixes.
 */
export const cmoNav: readonly NavItemDef[] = [
  { name: 'Channel Console', href: '/admin/channel-console', icon: ChartBarSquareIcon },
  {
    name: 'HELM',
    href: '/admin/cmo/helm',
    icon: SunIcon,
    activePrefixes: ['/admin/cmo/helm', '/admin/cmo/crm'],
  },
  { name: 'Short Links', href: '/admin/short-links', icon: LinkIcon },
];

/**
 * The CONTENT section (A7Y-4478, A7Y-4474): Publishing (the cross-section
 * drafts/scheduled/pending-edits queue), Docs (a placeholder until A7Y-4470),
 * the Blog CMS moved here from User Ops, and Keys — the per-person and
 * automation keys the content CLI (`npm run content`) authenticates with.
 * `/admin/content` itself redirects to Publishing, so no row targets it directly.
 */
export const contentNav: readonly NavItemDef[] = [
  { name: 'Publishing', href: '/admin/content/publishing', icon: RocketLaunchIcon },
  { name: 'Docs', href: '/admin/content/docs', icon: DocumentTextIcon },
  { name: 'Blog', href: '/admin/content/blog', icon: NewspaperIcon },
  { name: 'Keys', href: '/admin/content/keys', icon: KeyIcon },
];

/** Stats (A7Y-4883, A7Y-5020): read-only platform figures, one page per subject; Overview first, as in ADDIS. */
export const statsNav: readonly NavItemDef[] = [
  { name: 'Overview', href: '/admin/stats/overview', icon: TableCellsIcon },
  { name: 'Keyword Database', href: '/admin/stats/keyword-database', icon: CircleStackIcon },
];

/**
 * AI APIs (A7Y-5559): LLM API usage and spend by provider. Not the "AI Quota"
 * page under Ukko, which is the Claude/Codex subscription accounts. Provider
 * detail pages are reached from the Overview cards, so they have no row.
 */
export const aiApisNav: readonly NavItemDef[] = [
  {
    name: 'Overview',
    href: '/admin/ai-apis/overview',
    icon: ChartPieIcon,
    activePrefixes: ['/admin/ai-apis/overview', '/admin/ai-apis/providers'],
  },
  { name: 'Models', href: '/admin/ai-apis/models', icon: CubeTransparentIcon },
  { name: 'Tasks', href: '/admin/ai-apis/tasks', icon: ClipboardDocumentListIcon },
];
